import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
    Image, 
    Wand2, 
    Download, 
    RefreshCw, 
    Presentation, 
    Palette,
    History,
    Sparkles,
    Copy,
    ExternalLink
} from 'lucide-react';

const VisualAids = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(false);
    const [generatedImages, setGeneratedImages] = useState([]);
    const [history, setHistory] = useState([]);
    const [styles, setStyles] = useState(null);
    
    // Custom generation form
    const [customPrompt, setCustomPrompt] = useState('');
    const [negativePrompt, setNegativePrompt] = useState('');
    const [imageSize, setImageSize] = useState('1024x1024');
    const [imageCount, setImageCount] = useState(1);
    
    // Training visual form
    const [topicName, setTopicName] = useState('');
    const [visualType, setVisualType] = useState('illustration');
    const [visualStyle, setVisualStyle] = useState('professional');
    
    // Presentation form
    const [slideTitle, setSlideTitle] = useState('');
    const [slidePoints, setSlidePoints] = useState('');
    const [slideStyle, setSlideStyle] = useState('professional');

    useEffect(() => {
        fetchStyles();
        fetchHistory();
    }, []);

    const fetchStyles = async () => {
        try {
            const response = await api.get('/visuals/styles');
            setStyles(response.data);
        } catch (error) {
            console.error('Failed to fetch styles:', error);
        }
    };

    const fetchHistory = async () => {
        try {
            const response = await api.get('/visuals/history?limit=10');
            setHistory(response.data);
        } catch (error) {
            console.error('Failed to fetch history:', error);
        }
    };

    const generateCustomImage = async () => {
        if (!customPrompt.trim()) {
            toast.error('Please enter a prompt');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/visuals/generate', {
                prompt: customPrompt,
                negative_prompt: negativePrompt || undefined,
                n: imageCount,
                size: imageSize
            });
            
            setGeneratedImages(response.data.images);
            toast.success('Image generated successfully!');
            fetchHistory();
        } catch (error) {
            console.error('Image generation failed:', error);
            toast.error(error.response?.data?.detail || 'Image generation failed');
        } finally {
            setLoading(false);
        }
    };

    const generateTrainingVisual = async () => {
        if (!topicName.trim()) {
            toast.error('Please enter a topic name');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/visuals/training-visual', {
                topic_name: topicName,
                visual_type: visualType,
                style: visualStyle
            });
            
            setGeneratedImages(response.data.images);
            toast.success('Training visual generated!');
            fetchHistory();
        } catch (error) {
            console.error('Visual generation failed:', error);
            toast.error(error.response?.data?.detail || 'Visual generation failed');
        } finally {
            setLoading(false);
        }
    };

    const generatePresentationSlide = async () => {
        if (!slideTitle.trim()) {
            toast.error('Please enter a slide title');
            return;
        }

        const points = slidePoints.split('\n').filter(p => p.trim());

        setLoading(true);
        try {
            const response = await api.post('/visuals/presentation-slide', {
                title: slideTitle,
                content_points: points,
                style: slideStyle
            });
            
            setGeneratedImages(response.data.images);
            toast.success('Presentation slide background generated!');
            fetchHistory();
        } catch (error) {
            console.error('Slide generation failed:', error);
            toast.error(error.response?.data?.detail || 'Slide generation failed');
        } finally {
            setLoading(false);
        }
    };

    const copyImageUrl = (url) => {
        navigator.clipboard.writeText(url);
        toast.success('Image URL copied!');
    };

    const downloadImage = async (url, filename) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename || 'generated-image.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
            toast.success('Image downloaded!');
        } catch (error) {
            toast.error('Download failed');
        }
    };

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="visual-aids-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Image className="h-8 w-8 text-primary" />
                            Visual Aids Generator
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Generate images, diagrams, and presentation graphics for training
                        </p>
                    </div>
                    <Badge variant="outline" className="gap-1">
                        <Sparkles className="h-3 w-3" />
                        Powered by SDXL
                    </Badge>
                </div>

                <div className="grid gap-6 lg:grid-cols-3">
                    {/* Generation Forms */}
                    <div className="lg:col-span-2">
                        <Tabs defaultValue="custom" className="w-full">
                            <TabsList className="grid w-full grid-cols-3">
                                <TabsTrigger value="custom" className="gap-2">
                                    <Wand2 className="h-4 w-4" />
                                    Custom
                                </TabsTrigger>
                                <TabsTrigger value="training" className="gap-2">
                                    <Palette className="h-4 w-4" />
                                    Training Visual
                                </TabsTrigger>
                                <TabsTrigger value="presentation" className="gap-2">
                                    <Presentation className="h-4 w-4" />
                                    Slide
                                </TabsTrigger>
                            </TabsList>

                            {/* Custom Generation */}
                            <TabsContent value="custom">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Custom Image Generation</CardTitle>
                                        <CardDescription>
                                            Create any image from your text description
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Prompt *</label>
                                            <Textarea
                                                value={customPrompt}
                                                onChange={(e) => setCustomPrompt(e.target.value)}
                                                placeholder="Describe the image you want to generate..."
                                                rows={3}
                                                data-testid="custom-prompt"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Negative Prompt (optional)</label>
                                            <Input
                                                value={negativePrompt}
                                                onChange={(e) => setNegativePrompt(e.target.value)}
                                                placeholder="What to avoid in the image..."
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Size</label>
                                                <Select value={imageSize} onValueChange={setImageSize}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="1024x1024">Square (1024x1024)</SelectItem>
                                                        <SelectItem value="1792x1024">Landscape (1792x1024)</SelectItem>
                                                        <SelectItem value="1024x1792">Portrait (1024x1792)</SelectItem>
                                                        <SelectItem value="512x512">Small (512x512)</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Count</label>
                                                <Select value={String(imageCount)} onValueChange={(v) => setImageCount(Number(v))}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="1">1 Image</SelectItem>
                                                        <SelectItem value="2">2 Images</SelectItem>
                                                        <SelectItem value="4">4 Images</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        <Button 
                                            onClick={generateCustomImage} 
                                            disabled={loading || !customPrompt.trim()}
                                            className="w-full"
                                            data-testid="generate-custom"
                                        >
                                            {loading ? (
                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <Wand2 className="h-4 w-4 mr-2" />
                                            )}
                                            Generate Image
                                        </Button>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Training Visual */}
                            <TabsContent value="training">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Training Visual Generator</CardTitle>
                                        <CardDescription>
                                            Generate AI-enhanced visuals for training content
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Topic Name *</label>
                                            <Input
                                                value={topicName}
                                                onChange={(e) => setTopicName(e.target.value)}
                                                placeholder="e.g., Interview Techniques, Candidate Screening"
                                                data-testid="topic-name"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Visual Type</label>
                                                <Select value={visualType} onValueChange={setVisualType}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {styles?.visual_types?.map(vt => (
                                                            <SelectItem key={vt.id} value={vt.id}>
                                                                {vt.name}
                                                            </SelectItem>
                                                        )) || (
                                                            <>
                                                                <SelectItem value="illustration">Illustration</SelectItem>
                                                                <SelectItem value="icon">Icon</SelectItem>
                                                                <SelectItem value="scene">Scene</SelectItem>
                                                                <SelectItem value="diagram">Diagram</SelectItem>
                                                            </>
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Style</label>
                                                <Select value={visualStyle} onValueChange={setVisualStyle}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {styles?.styles?.map(s => (
                                                            <SelectItem key={s.id} value={s.id}>
                                                                {s.name}
                                                            </SelectItem>
                                                        )) || (
                                                            <>
                                                                <SelectItem value="professional">Professional</SelectItem>
                                                                <SelectItem value="infographic">Infographic</SelectItem>
                                                                <SelectItem value="realistic">Realistic</SelectItem>
                                                                <SelectItem value="cartoon">Cartoon</SelectItem>
                                                            </>
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        <Button 
                                            onClick={generateTrainingVisual} 
                                            disabled={loading || !topicName.trim()}
                                            className="w-full"
                                            data-testid="generate-training"
                                        >
                                            {loading ? (
                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <Palette className="h-4 w-4 mr-2" />
                                            )}
                                            Generate Training Visual
                                        </Button>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Presentation Slide */}
                            <TabsContent value="presentation">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Presentation Slide Background</CardTitle>
                                        <CardDescription>
                                            Generate professional slide backgrounds for presentations
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Slide Title *</label>
                                            <Input
                                                value={slideTitle}
                                                onChange={(e) => setSlideTitle(e.target.value)}
                                                placeholder="e.g., Introduction to Recruitment"
                                                data-testid="slide-title"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Content Points (one per line)</label>
                                            <Textarea
                                                value={slidePoints}
                                                onChange={(e) => setSlidePoints(e.target.value)}
                                                placeholder="Key point 1&#10;Key point 2&#10;Key point 3"
                                                rows={3}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Style</label>
                                            <Select value={slideStyle} onValueChange={setSlideStyle}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="professional">Professional</SelectItem>
                                                    <SelectItem value="modern">Modern</SelectItem>
                                                    <SelectItem value="creative">Creative</SelectItem>
                                                    <SelectItem value="dark">Dark</SelectItem>
                                                    <SelectItem value="colorful">Colorful</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <Button 
                                            onClick={generatePresentationSlide} 
                                            disabled={loading || !slideTitle.trim()}
                                            className="w-full"
                                            data-testid="generate-slide"
                                        >
                                            {loading ? (
                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <Presentation className="h-4 w-4 mr-2" />
                                            )}
                                            Generate Slide Background
                                        </Button>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>

                        {/* Generated Images Display */}
                        {generatedImages.length > 0 && (
                            <Card className="mt-6">
                                <CardHeader>
                                    <CardTitle>Generated Images</CardTitle>
                                    <CardDescription>
                                        URLs are valid for 12 hours. Download to save permanently.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        {generatedImages.map((img, idx) => (
                                            <div key={idx} className="relative group rounded-lg overflow-hidden border">
                                                <img 
                                                    src={img.url} 
                                                    alt={`Generated ${idx + 1}`}
                                                    className="w-full h-auto"
                                                />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                    <Button 
                                                        size="sm" 
                                                        variant="secondary"
                                                        onClick={() => copyImageUrl(img.url)}
                                                    >
                                                        <Copy className="h-4 w-4" />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="secondary"
                                                        onClick={() => downloadImage(img.url, `smart-trainer-${idx}.png`)}
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="secondary"
                                                        onClick={() => window.open(img.url, '_blank')}
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* History Sidebar */}
                    <div>
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <History className="h-5 w-5" />
                                    Recent Generations
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <ScrollArea className="h-[500px]">
                                    {history.length === 0 ? (
                                        <div className="p-6 text-center text-muted-foreground">
                                            <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">No images generated yet</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y">
                                            {history.map((item, idx) => (
                                                <div key={idx} className="p-4 hover:bg-muted/50">
                                                    <div className="aspect-video rounded overflow-hidden mb-2">
                                                        {item.result?.data?.[0]?.url ? (
                                                            <img 
                                                                src={item.result.data[0].url}
                                                                alt="Generated"
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full bg-muted flex items-center justify-center">
                                                                <Image className="h-6 w-6 text-muted-foreground" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground line-clamp-2">
                                                        {item.prompt || item.topic_name || item.title || 'Generated image'}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {new Date(item.created_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </ScrollArea>
                            </CardContent>
                        </Card>

                        {/* Tips Card */}
                        <Card className="mt-4 bg-primary/5 border-primary/20">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium">Prompt Tips</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="text-xs text-muted-foreground space-y-2">
                                    <li className="flex gap-2">
                                        <span className="text-primary">•</span>
                                        Be specific and descriptive
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-primary">•</span>
                                        Include style keywords (professional, modern)
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-primary">•</span>
                                        Use negative prompts to exclude unwanted elements
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-primary">•</span>
                                        Try different sizes for various uses
                                    </li>
                                </ul>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default VisualAids;
